import { html, LitElement } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { spread } from '@open-wc/lit-helpers';
import { property, query } from 'lit/decorators.js';
import '@material/web/checkbox/checkbox.js';
import { Checkbox } from '@material/web/checkbox/internal/checkbox.js';
import { AriaForwardMixin } from '@digital-realty/ix-shared-fns/aria-forward-mixin.js';

const AccessibilityBase = AriaForwardMixin(LitElement);

export class IxCheckbox extends AccessibilityBase {
  @query('.checkbox') readonly component!: Checkbox;

  @property({ type: Boolean }) disabled = false;

  @property({ type: Boolean }) checked = false;

  @property({ type: Boolean }) error = false;

  @property({ type: Boolean }) indeterminate = false;

  @property({ type: Boolean }) required = false;

  @property() label: string = '';

  @property({ attribute: 'aria-label' }) ariaLabel: string | null = null;

  @property({ attribute: 'touch-target' }) target: 'wrapper' | '' = '';

  @property({ attribute: 'html-id' }) htmlId: string | undefined = undefined;

  @property() value = 'on';

  @property() name = '';

  focus() {
    this.component.focus();
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (!this.ariaAttributes.ariaLabel && this.label) {
      this.ariaAttributes = {
        ...this.ariaAttributes,
        'aria-label': this.label,
      };
    }
  }

  protected createRenderRoot() {
    return this;
  }

  get renderCheckbox() {
    return html`
      <md-checkbox
        class="checkbox"
        ${spread(this.ariaAttributes)}
        ?checked=${this.checked}
        ?disabled=${this.disabled}
        ?indeterminate=${this.indeterminate}
        touch-target=${this.target}
        id=${ifDefined(this.htmlId)}
        ?required=${this.required}
        name=${this.name}
      ></md-checkbox>
    `;
  }

  render() {
    if (this.label)
      return html` <style>
          [error] {
            --md-outlined-field-outline-width: 2px;
          }
          .checkbox {
            flex-shrink: 0;
          }
          .checkbox-label {
            align-items: center;
            display: flex;
            gap: var(--ix-checkbox-radio-gap, 0.5rem);
          }
          .checkbox-label[error] {
            color: var(
              --ix-checkbox-clr-critical,
              var(--clr-critical, #db0028)
            );
            --md-checkbox-outline-color: var(
              --ix-checkbox-clr-critical,
              var(--clr-critical, #db0028)
            );
            --md-checkbox-focus-outline-color: var(
              --ix-checkbox-clr-critical,
              var(--clr-critical, #db0028)
            );
            --md-checkbox-hover-outline-color: var(
              --ix-checkbox-clr-critical,
              var(--clr-critical, #db0028)
            );
          }
          .checkbox-label[disabled] {
            color: var(
              --ix-checkbox-clr-disabled,
              var(--clr-on-surface-disabled, #09224180)
            );
          }
          [indeterminate] {
            --md-sys-color-primary: var(--clr-form-outline, #0922411f);
            --md-checkbox-outline-color: transparent;
            --md-checkbox-focus-outline-color: var(
              --ix-checkbox-indeterminante-focus-outline-color,
              transparent
            );
            --md-checkbox-hover-outline-color: var(
              --ix-checkbox-indeterminante-hover-outline-color,
              transparent
            );
          }
        </style>
        <label
          class="checkbox-label"
          ?disabled=${this.disabled}
          ?error=${this.error}
          >${this.renderCheckbox}<span>${this.label}</span></label
        >`;

    return this.renderCheckbox;
  }
}
